import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Video, MapPin, X, ChevronLeft, ChevronRight, User, Mail, Phone, Globe, Check, ArrowRight, Users, Star, Zap, AlertCircle, Loader } from 'lucide-react';

const ScheduleConsultation = ({ showScheduling, setShowScheduling, language = 'en' }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentStep, setCurrentStep] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [busyTimes, setBusyTimes] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [calendarError, setCalendarError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    meetingType: '',
    timezone: 'PST',
    additionalRequests: '',
    investmentInterest: '',
    preferredTime: ''
  });

  // Google Calendar API configuration - DEBUG MODE
  const GOOGLE_CALENDAR_ID = 'zeus@infinitydevelopment.com'; // Zeus's confirmed email
  const GOOGLE_API_KEY = 'AIzaSyAGmkUpvRAZcPXr1RDSaMuuPshwwq4IWW4'; // Make sure this is your real API key
  
  // DEBUG: Log what we're trying to access
  console.log('Trying to access calendar:', GOOGLE_CALENDAR_ID);
  console.log('API Key configured:', GOOGLE_API_KEY !== 'YOUR_ACTUAL_API_KEY_HERE');

  // All possible time slots (will be filtered based on availability)
  const allTimeSlots = [
    '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '12:00 PM', '12:30 PM', '01:00 PM', '01:30 PM', '02:00 PM', '02:30 PM',
    '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM', '05:00 PM'
  ];

  const meetingTypes = [
    {
      id: 'initial',
      name: language === 'en' ? 'Initial Investment Consultation' : 'Consulta Inicial de Inversión',
      duration: 15, // Changed to number for calculations
      durationDisplay: '15 min',
      description: language === 'en' ? 'Perfect for first-time investors. Overview of opportunities and investment process.' : 'Perfecto para inversionistas primerizos. Resumen de oportunidades y proceso de inversión.',
      icon: Users,
      color: 'from-blue-500 to-blue-600',
      features: language === 'en' ? ['Investment overview', 'Q&A session', 'Next steps guidance'] : ['Resumen de inversiones', 'Sesión de preguntas', 'Orientación de próximos pasos']
    },
    {
      id: 'detailed',
      name: language === 'en' ? 'Detailed Investment Review' : 'Revisión Detallada de Inversión',
      duration: 60,
      durationDisplay: '60 min',
      description: language === 'en' ? 'Comprehensive review for serious investors. Deep dive into specific projects and portfolios.' : 'Revisión integral para inversionistas serios. Análisis profundo de proyectos específicos y portafolios.',
      icon: Star,
      color: 'from-purple-500 to-purple-600',
      features: language === 'en' ? ['Project analysis', 'Financial projections', 'Risk assessment', 'Investment strategy'] : ['Análisis de proyectos', 'Proyecciones financieras', 'Evaluación de riesgo', 'Estrategia de inversión']
    },
    {
      id: 'portfolio',
      name: language === 'en' ? 'Portfolio Strategy Session' : 'Sesión de Estrategia de Portafolio',
      duration: 45,
      durationDisplay: '45 min',
      description: language === 'en' ? 'Customized portfolio planning for high-net-worth individuals.' : 'Planificación personalizada de portafolio para individuos de alto patrimonio.',
      icon: Zap,
      color: 'from-green-500 to-green-600',
      features: language === 'en' ? ['Portfolio optimization', 'Diversification strategy', 'Tax considerations', 'Exit planning'] : ['Optimización de portafolio', 'Estrategia de diversificación', 'Consideraciones fiscales', 'Planificación de salida']
    }
  ];

  const timezones = [
    { value: 'PST', label: 'Pacific Standard Time (PST)' },
    { value: 'MST', label: 'Mountain Standard Time (MST)' },
    { value: 'CST', label: 'Central Standard Time (CST)' },
    { value: 'EST', label: 'Eastern Standard Time (EST)' },
    { value: 'GMT', label: 'Greenwich Mean Time (GMT)' },
    { value: 'CET', label: 'Central European Time (CET)' }
  ];

  const steps = [
    { id: 1, title: language === 'en' ? 'Meeting Type' : 'Tipo de Reunión' },
    { id: 2, title: language === 'en' ? 'Date & Time' : 'Fecha y Hora' },
    { id: 3, title: language === 'en' ? 'Your Details' : 'Tus Detalles' },
    { id: 4, title: language === 'en' ? 'Confirmation' : 'Confirmación' }
  ];

  // Function to check Google Calendar availability
  const checkCalendarAvailability = async (date) => {
    if (!GOOGLE_API_KEY || GOOGLE_API_KEY === 'YOUR_API_KEY_HERE') {
      // Demo mode - simulate some busy times for testing
      console.log('Demo mode: Using simulated busy times');
      const demoBusyTimes = [
        {
          start: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 10, 0),
          end: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 11, 0)
        },
        {
          start: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 14, 0),
          end: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 15, 30)
        }
      ];
      setBusyTimes(demoBusyTimes);
      setCalendarError('Demo mode - Replace with your API key for real calendar data');
      return demoBusyTimes;
    }

    setLoadingAvailability(true);
    setCalendarError(null);

    try {
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);

      const timeMin = startOfDay.toISOString();
      const timeMax = endOfDay.toISOString();

      const response = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(GOOGLE_CALENDAR_ID)}/events?` +
        new URLSearchParams({
          key: GOOGLE_API_KEY,
          timeMin,
          timeMax,
          singleEvents: 'true',
          orderBy: 'startTime'
        })
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Calendar API error ${response.status}: ${errorData.error?.message || 'Unknown error'}`);
      }

      const data = await response.json();
      
      // Extract busy times from events
      const busy = data.items
        .filter(event => event.start && event.end)
        .map(event => ({
          start: new Date(event.start.dateTime || event.start.date),
          end: new Date(event.end.dateTime || event.end.date),
          summary: event.summary || 'Busy'
        }));

      setBusyTimes(busy);
      console.log(`Found ${busy.length} events for ${date.toDateString()}`);
      return busy;
    } catch (error) {
      console.error('Error fetching calendar availability:', error);
      
      let errorMessage = 'Unable to check calendar availability. Showing all time slots.';
      
      if (error.message.includes('403')) {
        errorMessage = 'Calendar access denied. Please make sure Zeus\'s calendar is public or properly shared.';
      } else if (error.message.includes('404')) {
        errorMessage = 'Calendar not found. Please check the calendar email address.';
      } else if (error.message.includes('400')) {
        errorMessage = 'Invalid API key or request. Please check your Google Calendar API configuration.';
      }
      
      setCalendarError(errorMessage);
      setBusyTimes([]);
      return [];
    } finally {
      setLoadingAvailability(false);
    }
  };

  // Function to check if a time slot is available
  const isTimeSlotAvailable = (date, timeSlot) => {
    if (!date) return false;

    const [time, period] = timeSlot.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    
    const slotStart = new Date(date);
    slotStart.setHours(hours, minutes, 0, 0);
    
    const selectedMeeting = meetingTypes.find(m => m.id === formData.meetingType);
    const duration = selectedMeeting ? selectedMeeting.duration : 30;
    
    const slotEnd = new Date(slotStart);
    slotEnd.setMinutes(slotEnd.getMinutes() + duration);

    // Check if this slot conflicts with any busy time
    const hasConflict = busyTimes.some(busy => {
      return (slotStart < busy.end && slotEnd > busy.start);
    });

    return !hasConflict;
  };

  // Get available time slots for the selected date
  const getAvailableTimeSlots = () => {
    if (!selectedDate) return [];
    
    return allTimeSlots.filter(timeSlot => isTimeSlotAvailable(selectedDate, timeSlot));
  };

  useEffect(() => {
    if (showScheduling) {
      setCurrentStep(1);
      setSelectedDate(null);
      setSelectedTime(null);
      setBusyTimes([]);
      setCalendarError(null);
    }
  }, [showScheduling]);

  // Check availability when date is selected
  useEffect(() => {
    if (selectedDate && formData.meetingType) {
      checkCalendarAvailability(selectedDate);
      setSelectedTime(null); // Reset selected time when date changes
    }
  }, [selectedDate, formData.meetingType]);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
        setIsAnimating(false);
      }, 150);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(prev => prev - 1);
        setIsAnimating(false);
      }, 150);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.meetingType;
      case 2:
        return selectedDate && selectedTime;
      case 3:
        return formData.name && formData.email && formData.phone;
      default:
        return true;
    }
  };

  const generateGoogleCalendarLink = () => {
    if (!selectedDate || !selectedTime || !formData.meetingType) return '';

    const meeting = meetingTypes.find(m => m.id === formData.meetingType);
    const startDate = new Date(selectedDate);
    const [time, period] = selectedTime.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    
    startDate.setHours(hours, minutes, 0, 0);
    
    const endDate = new Date(startDate);
    endDate.setMinutes(endDate.getMinutes() + meeting.duration);

    const formatDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const details = `Investment consultation with Infinity Development.%0A%0AAgenda:%0A${meeting.features.map(f => `• ${f}`).join('%0A')}%0A%0AZoom link will be provided via email.`;

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: `${meeting.name} - Infinity Development`,
      dates: `${formatDate(startDate)}/${formatDate(endDate)}`,
      details: details,
      location: 'Zoom Meeting (link via email)',
      sf: 'true',
      output: 'xml'
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  const handleSubmit = async () => {
    const meetingData = {
      ...formData,
      selectedDate,
      selectedTime,
      meetingType: meetingTypes.find(type => type.id === formData.meetingType),
      googleCalendarLink: generateGoogleCalendarLink()
    };
    
    console.log('Meeting scheduled:', meetingData);
    
    // Here you would typically send this data to your backend to:
    // 1. Create the calendar event in Zeus's calendar
    // 2. Send confirmation emails
    // 3. Generate Zoom links
    
    alert(language === 'en' 
      ? `Meeting scheduled for ${selectedDate.toLocaleDateString()} at ${selectedTime}. You will receive a Zoom link and calendar invitation shortly.`
      : `Reunión programada para ${selectedDate.toLocaleDateString()} a las ${selectedTime}. Recibirás un enlace de Zoom y una invitación de calendario en breve.`);
    
    if (generateGoogleCalendarLink()) {
      window.open(generateGoogleCalendarLink(), '_blank');
    }
    
    // Reset form
    setShowScheduling(false);
    setCurrentStep(1);
    setSelectedDate(null);
    setSelectedTime(null);
    setBusyTimes([]);
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      meetingType: '',
      timezone: 'PST',
      additionalRequests: '',
      investmentInterest: '',
      preferredTime: ''
    });
  };

  // Calendar functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const isDateAvailable = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayOfWeek = date.getDay();
    return date >= today && dayOfWeek >= 1 && dayOfWeek <= 5; // Monday to Friday only
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-12"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const isAvailable = isDateAvailable(date);
      const isSelected = selectedDate && selectedDate.toDateString() === date.toDateString();
      const isToday = new Date().toDateString() === date.toDateString();

      days.push(
        <button
          key={day}
          onClick={() => isAvailable && setSelectedDate(date)}
          disabled={!isAvailable}
          className={`h-12 w-12 rounded-xl text-sm font-medium transition-all duration-200 ${
            isSelected
              ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg transform scale-105'
              : isAvailable
              ? `text-white hover:bg-gradient-to-r hover:from-blue-500/20 hover:to-purple-500/20 border border-gray-600 hover:border-blue-400 ${isToday ? 'border-blue-400' : ''}`
              : 'text-gray-600 cursor-not-allowed'
          }`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-white mb-4">
                {language === 'en' ? 'Choose Your Consultation Type' : 'Elige tu Tipo de Consulta'}
              </h3>
              <p className="text-gray-400 text-lg">
                {language === 'en' 
                  ? 'Select the consultation that best fits your investment goals'
                  : 'Selecciona la consulta que mejor se adapte a tus objetivos de inversión'}
              </p>
            </div>
            
            <div className="grid gap-6">
              {meetingTypes.map((meeting) => {
                const IconComponent = meeting.icon;
                const isSelected = formData.meetingType === meeting.id;
                
                return (
                  <div
                    key={meeting.id}
                    onClick={() => handleFormChange('meetingType', meeting.id)}
                    className={`p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                      isSelected
                        ? 'border-blue-400 bg-gradient-to-r from-blue-500/10 to-purple-500/10 shadow-lg'
                        : 'border-gray-600 hover:border-gray-500 bg-gray-800/50'
                    }`}
                  >
                    <div className="flex items-start space-x-4">
                      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-r ${meeting.color} flex items-center justify-center flex-shrink-0`}>
                        <IconComponent size={24} className="text-white" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xl font-bold text-white">{meeting.name}</h4>
                          <div className="flex items-center space-x-2">
                            <Clock size={16} className="text-blue-400" />
                            <span className="text-blue-400 font-medium">{meeting.durationDisplay}</span>
                          </div>
                        </div>
                        
                        <p className="text-gray-300 mb-4 leading-relaxed">{meeting.description}</p>
                        
                        <div className="flex flex-wrap gap-2">
                          {meeting.features.map((feature, index) => (
                            <span key={index} className="px-3 py-1 bg-gray-700 text-gray-300 text-sm rounded-full">
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      {isSelected && (
                        <div className="flex-shrink-0">
                          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                            <Check size={14} className="text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 2:
        const availableTimeSlots = getAvailableTimeSlots();
        
        return (
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Calendar */}
            <div className="bg-gray-800/50 p-6 rounded-2xl border border-gray-700">
              <h4 className="text-2xl font-bold mb-6 text-white flex items-center">
                <Calendar size={24} className="mr-3 text-green-400" />
                {language === 'en' ? 'Select Date' : 'Seleccionar Fecha'}
              </h4>
              
              {/* Calendar Navigation */}
              <div className="flex justify-between items-center mb-6">
                <button
                  type="button"
                  onClick={() => navigateMonth(-1)}
                  className="p-3 hover:bg-gray-700 rounded-xl transition-colors"
                >
                  <ChevronLeft size={20} className="text-white" />
                </button>
                <h5 className="text-xl font-bold text-white">
                  {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h5>
                <button
                  type="button"
                  onClick={() => navigateMonth(1)}
                  className="p-3 hover:bg-gray-700 rounded-xl transition-colors"
                >
                  <ChevronRight size={20} className="text-white" />
                </button>
              </div>

              {/* Days of week header */}
              <div className="grid grid-cols-7 gap-2 mb-4">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-400 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-2">
                {renderCalendar()}
              </div>

              <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                <p className="text-sm text-blue-300 flex items-center">
                  <Clock size={16} className="mr-2" />
                  {language === 'en' 
                    ? 'Available: Monday - Friday, 9:00 AM - 5:00 PM PST'
                    : 'Disponible: Lunes - Viernes, 9:00 AM - 5:00 PM PST'}
                </p>
              </div>

              {/* Calendar Error or Status */}
              {calendarError && (
                <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/20 rounded-xl">
                  <div className="flex items-start space-x-2">
                    <AlertCircle size={16} className="text-orange-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-orange-300">{calendarError}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Time Selection */}
            <div className="bg-gray-800/50 p-6 rounded-2xl border border-gray-700">
              <h4 className="text-2xl font-bold mb-6 text-white flex items-center">
                <Clock size={24} className="mr-3 text-purple-400" />
                {language === 'en' ? 'Select Time' : 'Seleccionar Hora'}
                {loadingAvailability && <Loader size={16} className="ml-2 animate-spin text-purple-400" />}
              </h4>
              
              {selectedDate ? (
                <div>
                  <div className="mb-6 p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
                    <p className="text-purple-300 font-medium">
                      {language === 'en' ? 'Selected date:' : 'Fecha seleccionada:'} {selectedDate.toLocaleDateString()}
                    </p>
                    {busyTimes.length > 0 && (
                      <p className="text-xs text-gray-400 mt-1">
                        {language === 'en' ? 'Times filtered based on calendar availability' : 'Horarios filtrados según disponibilidad del calendario'}
                      </p>
                    )}
                  </div>
                  
                  {availableTimeSlots.length > 0 ? (
                    <div className="grid grid-cols-2 gap-3 max-h-80 overflow-y-auto pr-2">
                      {availableTimeSlots.map((time) => (
                        <button
                          key={time}
                          type="button"
                          onClick={() => setSelectedTime(time)}
                          className={`p-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                            selectedTime === time
                              ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg transform scale-105'
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600 hover:border-purple-400'
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <AlertCircle size={48} className="text-gray-600 mb-4" />
                      <p className="text-gray-400 text-lg mb-2">
                        {language === 'en' 
                          ? 'No available times for this date'
                          : 'No hay horarios disponibles para esta fecha'}
                      </p>
                      <p className="text-gray-500 text-sm">
                        {language === 'en' 
                          ? 'Please select a different date'
                          : 'Por favor selecciona una fecha diferente'}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Calendar size={48} className="text-gray-600 mb-4" />
                  <p className="text-gray-400 text-lg">
                    {language === 'en' 
                      ? 'Please select a date first'
                      : 'Por favor selecciona una fecha primero'}
                  </p>
                </div>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-white mb-4">
                {language === 'en' ? 'Your Contact Information' : 'Tu Información de Contacto'}
              </h3>
              <p className="text-gray-400 text-lg">
                {language === 'en' 
                  ? 'Help us prepare for your consultation'
                  : 'Ayúdanos a prepararnos para tu consulta'}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  {language === 'en' ? 'Full Name *' : 'Nombre Completo *'}
                </label>
                <div className="relative">
                  <User size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-gray-700 border border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white transition-all"
                    placeholder={language === 'en' ? 'John Doe' : 'Juan Pérez'}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  {language === 'en' ? 'Email Address *' : 'Correo Electrónico *'}
                </label>
                <div className="relative">
                  <Mail size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleFormChange('email', e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-gray-700 border border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white transition-all"
                    placeholder={language === 'en' ? 'john@example.com' : 'juan@ejemplo.com'}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  {language === 'en' ? 'Phone Number *' : 'Número de Teléfono *'}
                </label>
                <div className="relative">
                  <Phone size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => handleFormChange('phone', e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-gray-700 border border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white transition-all"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  {language === 'en' ? 'Time Zone' : 'Zona Horaria'}
                </label>
                <div className="relative">
                  <Globe size={20} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <select
                    value={formData.timezone}
                    onChange={(e) => handleFormChange('timezone', e.target.value)}
                    className="w-full pl-12 pr-4 py-4 bg-gray-700 border border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white transition-all appearance-none"
                  >
                    {timezones.map(tz => (
                      <option key={tz.value} value={tz.value}>{tz.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                {language === 'en' ? 'Company/Organization' : 'Empresa/Organización'}
              </label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => handleFormChange('company', e.target.value)}
                className="w-full px-4 py-4 bg-gray-700 border border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white transition-all"
                placeholder={language === 'en' ? 'Your company name (optional)' : 'Nombre de tu empresa (opcional)'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                {language === 'en' ? 'Investment Interest Level' : 'Nivel de Interés de Inversión'}
              </label>
              <select
                value={formData.investmentInterest}
                onChange={(e) => handleFormChange('investmentInterest', e.target.value)}
                className="w-full px-4 py-4 bg-gray-700 border border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white transition-all"
              >
                <option value="">
                  {language === 'en' ? 'Select your interest level...' : 'Selecciona tu nivel de interés...'}
                </option>
                <option value="exploring">
                  {language === 'en' ? 'Just exploring options' : 'Solo explorando opciones'}
                </option>
                <option value="serious">
                  {language === 'en' ? 'Serious about investing' : 'Serio sobre invertir'}
                </option>
                <option value="ready">
                  {language === 'en' ? 'Ready to invest soon' : 'Listo para invertir pronto'}
                </option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                {language === 'en' ? 'Additional Notes' : 'Notas Adicionales'}
              </label>
              <textarea
                rows={4}
                value={formData.additionalRequests}
                onChange={(e) => handleFormChange('additionalRequests', e.target.value)}
                className="w-full px-4 py-4 bg-gray-700 border border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white resize-none transition-all"
                placeholder={language === 'en' 
                  ? 'Any specific topics you\'d like to discuss or questions you have...'
                  : 'Cualquier tema específico que te gustaría discutir o preguntas que tengas...'}
              />
            </div>
          </div>
        );

      case 4:
        const selectedMeeting = meetingTypes.find(m => m.id === formData.meetingType);
        return (
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check size={32} className="text-white" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">
                {language === 'en' ? 'Confirm Your Consultation' : 'Confirma tu Consulta'}
              </h3>
              <p className="text-gray-400 text-lg">
                {language === 'en' 
                  ? 'Please review your booking details'
                  : 'Por favor revisa los detalles de tu reserva'}
              </p>
            </div>

            <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-2xl p-8 space-y-6">
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-r ${selectedMeeting.color} flex items-center justify-center`}>
                  <selectedMeeting.icon size={20} className="text-white" />
                </div>
                <div>
                  <h4 className="text-xl font-bold text-white">{selectedMeeting.name}</h4>
                  <p className="text-gray-400">{selectedMeeting.durationDisplay} consultation</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Calendar size={20} className="text-blue-400" />
                    <div>
                      <p className="text-sm text-gray-400">Date</p>
                      <p className="text-white font-medium">{selectedDate?.toLocaleDateString()}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Clock size={20} className="text-blue-400" />
                    <div>
                      <p className="text-sm text-gray-400">Time</p>
                      <p className="text-white font-medium">{selectedTime} ({formData.timezone})</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <User size={20} className="text-blue-400" />
                    <div>
                      <p className="text-sm text-gray-400">Contact</p>
                      <p className="text-white font-medium">{formData.name}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Video size={20} className="text-blue-400" />
                    <div>
                      <p className="text-sm text-gray-400">Platform</p>
                      <p className="text-white font-medium">
                        {language === 'en' ? 'Zoom Meeting' : 'Reunión por Zoom'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-gray-700">
                <h5 className="text-lg font-semibold text-white mb-3">
                  {language === 'en' ? 'What to expect:' : 'Qué esperar:'}
                </h5>
                <ul className="space-y-2">
                  {selectedMeeting.features.map((feature, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <Check size={16} className="text-green-400" />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="pt-6 border-t border-gray-700">
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                  <p className="text-sm text-blue-300 leading-relaxed">
                    <strong>{language === 'en' ? 'Next steps:' : 'Próximos pasos:'}</strong><br />
                    {language === 'en'
                      ? 'You\'ll receive a Zoom link and calendar invitation via email within 5 minutes. The meeting will also be added to your Google Calendar automatically.'
                      : 'Recibirás un enlace de Zoom y una invitación de calendario por correo electrónico en 5 minutos. La reunión también se agregará automáticamente a tu Google Calendar.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!showScheduling) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-3xl max-w-6xl w-full max-h-[95vh] overflow-hidden border border-gray-700">
        {/* Header */}
        <div className="p-8 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-900">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-3xl font-bold text-white">
                {language === 'en' ? 'Schedule Your Consultation' : 'Programa tu Consulta'}
              </h2>
              <p className="text-gray-400 mt-2">
                {language === 'en' 
                  ? 'Book a personalized investment consultation with our experts'
                  : 'Reserva una consulta de inversión personalizada con nuestros expertos'}
              </p>
            </div>
            <button 
              onClick={() => setShowScheduling(false)}
              className="text-gray-400 hover:text-white text-2xl p-2 hover:bg-gray-800 rounded-xl transition-all"
            >
              <X size={24} />
            </button>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center space-x-4">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                    currentStep === step.id
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                      : currentStep > step.id
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-700 text-gray-400'
                  }`}>
                    {currentStep > step.id ? <Check size={16} /> : step.id}
                  </div>
                  <span className={`hidden md:block text-sm font-medium transition-colors ${
                    currentStep >= step.id ? 'text-white' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`hidden md:block h-0.5 w-12 transition-colors ${
                    currentStep > step.id ? 'bg-green-500' : 'bg-gray-700'
                  }`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto max-h-[calc(95vh-200px)]">
          <div className={`transition-all duration-300 ${
            isAnimating ? 'opacity-0 transform translate-x-4' : 'opacity-100 transform translate-x-0'
          }`}>
            {renderStep()}
          </div>
        </div>

        {/* Footer */}
        <div className="p-8 border-t border-gray-700 bg-gray-800/50">
          <div className="flex justify-between items-center">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${
                currentStep === 1
                  ? 'text-gray-500 cursor-not-allowed'
                  : 'text-gray-300 hover:text-white hover:bg-gray-700'
              }`}
            >
              <ChevronLeft size={20} />
              <span>{language === 'en' ? 'Previous' : 'Anterior'}</span>
            </button>

            <div className="flex items-center space-x-4">
              {currentStep < steps.length && (
                <span className="text-sm text-gray-500 hidden md:block">
                  {language === 'en' ? 'Step' : 'Paso'} {currentStep} {language === 'en' ? 'of' : 'de'} {steps.length}
                </span>
              )}

              {currentStep === steps.length ? (
                <button
                  onClick={handleSubmit}
                  className="flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-green-500 to-blue-500 text-white font-bold rounded-xl hover:from-green-600 hover:to-blue-600 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  <Calendar size={20} />
                  <span>{language === 'en' ? 'Confirm & Schedule' : 'Confirmar y Programar'}</span>
                  <ArrowRight size={20} />
                </button>
              ) : (
                <button
                  onClick={nextStep}
                  disabled={!canProceed()}
                  className={`flex items-center space-x-3 px-8 py-4 rounded-xl font-bold transition-all duration-300 ${
                    canProceed()
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600 transform hover:scale-105 shadow-lg'
                      : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <span>{language === 'en' ? 'Continue' : 'Continuar'}</span>
                  <ArrowRight size={20} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleConsultation;